import { useState } from 'react'
import type { ReactNode } from 'react'
import { router } from '@inertiajs/react'
import { CalendarDays, Heart, ArrowRight, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import PaymentModeCards, { type PaymentMode } from '@/components/event-form/PaymentModeCards'

type CampaignKind = 'event' | 'fundraiser'

type NewCampaignDialogProps = {
  trigger: ReactNode
  onNavigate?: () => void
}

const OPTIONS: {
  kind: CampaignKind
  title: string
  description: string
  bullets: string[]
  icon: typeof CalendarDays
}[] = [
  {
    kind: 'event',
    title: 'Event page',
    description: 'Ticketed gala, dinner, auction, or in-person gathering.',
    bullets: ['Tickets and RSVP', 'Sponsors and program ads', 'Hosts, honorees, committee'],
    icon: CalendarDays,
  },
  {
    kind: 'fundraiser',
    title: 'Fundraising page',
    description: 'Donation-first campaign without event logistics.',
    bullets: ['Native or external donations', 'Simple supporter flow', 'No date or venue required'],
    icon: Heart,
  },
]

function isValidHttpsUrl(value: string): boolean {
  try {
    return new URL(value.trim()).protocol === 'https:'
  } catch {
    return false
  }
}

export default function NewCampaignDialog({ trigger, onNavigate }: NewCampaignDialogProps) {
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState<1 | 2>(1)
  const [kind, setKind] = useState<CampaignKind>('event')
  const [mode, setMode] = useState<PaymentMode>('native')
  const [externalUrl, setExternalUrl] = useState('')

  const externalReady = mode === 'native' || isValidHttpsUrl(externalUrl)

  function reset() {
    setStep(1)
    setKind('event')
    setMode('native')
    setExternalUrl('')
  }

  function handleOpenChange(next: boolean) {
    setOpen(next)
    if (!next) reset()
  }

  function continueToCampaign() {
    setOpen(false)
    reset()
    onNavigate?.()
    const params = new URLSearchParams({ page_kind: kind, payment_mode: mode })
    if (mode === 'external') params.set('external_url', externalUrl.trim())
    router.visit(`/dashboard/events/new?${params.toString()}`)
  }

  const modeTitle = kind === 'fundraiser'
    ? 'How should people give?'
    : 'How should people give or get tickets?'

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {step === 1 ? 'Create a new campaign' : modeTitle}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? 'Pick the campaign type. You can edit the content and settings after this.'
              : 'Choose built-in payments or link the platform you already use. You can switch later.'}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {OPTIONS.map((option) => {
              const Icon = option.icon
              const active = kind === option.kind
              return (
                <button
                  key={option.kind}
                  type="button"
                  onClick={() => setKind(option.kind)}
                  className={`rounded-lg border bg-card p-4 text-left transition ${
                    active
                      ? 'border-primary ring-2 ring-primary/30'
                      : 'border-black/10 hover:border-primary/40'
                  }`}
                >
                  <span className={`inline-flex size-10 items-center justify-center rounded-md ${
                    active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    <Icon className="size-5" />
                  </span>
                  <span className="mt-4 block font-serif text-xl font-semibold">{option.title}</span>
                  <span className="mt-1 block text-sm text-muted-foreground">{option.description}</span>
                  <span className="mt-4 block space-y-1 text-sm text-muted-foreground">
                    {option.bullets.map((bullet) => (
                      <span key={bullet} className="block">+ {bullet}</span>
                    ))}
                  </span>
                </button>
              )
            })}
          </div>
        ) : (
          <PaymentModeCards
            kind={kind}
            mode={mode}
            onModeChange={setMode}
            externalUrl={externalUrl}
            onExternalUrlChange={setExternalUrl}
          />
        )}

        <DialogFooter>
          {step === 1 ? (
            <>
              <DialogClose asChild>
                <Button type="button" variant="ghost">Cancel</Button>
              </DialogClose>
              <Button type="button" onClick={() => setStep(2)}>
                Continue
                <ArrowRight className="size-4" />
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={() => setStep(1)}>
                <ArrowLeft className="size-4" />
                Back
              </Button>
              <Button type="button" onClick={continueToCampaign} disabled={!externalReady}>
                Continue
                <ArrowRight className="size-4" />
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
